import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSPrototypes = () => {
    const prototypeChainCode = `// The Prototype Chain in action
const hardware = {
    brand: 'NVIDIA',
    type: 'GPU',
    specs() {
        return \`\${this.brand} \${this.type}\`;
    }
};

const vRAM = {
    size: '24GB',
    // Setting inheritance via Object.create
    __proto__: hardware
};

console.log(vRAM.size);  // '24GB' (Own Property)
console.log(vRAM.brand); // 'NVIDIA' (Inherited via Chain)
console.log(vRAM.specs()); // 'NVIDIA GPU' (Inherited Method)`;

    const memoryEfficiencyCode = `// --- Prototype vs Instance Methods ---

function OptimizedObject(id) {
    this.id = id;
}

// Memory Efficient: All instances share 1 function reference
OptimizedObject.prototype.logId = function() {
    console.log(this.id);
};

function InefficientObject(id) {
    this.id = id;
    //  Memory Leak: Every instance gets a custom function clone
    this.logId = function() {
        console.log(this.id);
    };
}`;

    const nullPrototypeCode = `// Object.create(null) - The High Performance Dictionary
const dictionary = Object.create(null);

// Standard objects have inherited methods like .toString()
console.log({}.toString); // [Function: toString]

// A null prototype object is "Pure" - no inheritance risk
console.log(dictionary.toString); // undefined

//  Ideal for maps where you don't want property pollution.`;

    const internalMechanicsCode = `// How 'new' actually works under the hood
function User(name) {
    this.name = name;
}

const u1 = new User('Alice');

/* 
  1. {} is created
  2. {}.__proto__ = User.prototype
  3. User.call({}, 'Alice')
  4. The object is returned
*/

console.log(u1.__proto__ === User.prototype); // true`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">OOP & Inheritance</div>
                <h1>Prototypal Inheritance</h1>
                <p className="hero-lead">
                    JavaScript does not use traditional class-based inheritance like Java
                    or C++. Instead, it uses <strong>Prototypes</strong>a chain of
                    linked objects that allow for sophisticated behavior sharing and
                    exceptional memory efficiency.
                </p>
            </div>

            <div className="content-section">
                <h2>The Prototype Chain</h2>
                <p>
                    Every object in JavaScript has a internal link to another object
                    called its <strong>Prototype</strong>. When you access a property
                    that doesn't exist on the object, the engine "walks up" this chain
                    until it finds the property or hits <code>null</code>.
                </p>
                <CodeBlock language="javascript" code={prototypeChainCode} />
            </div>

            <div className="content-section">
                <h2>Memory Efficiency: Prototype vs Instance</h2>
                <p>
                    Understanding prototypes is crucial for performance. Methods added
                    to a function's <code>prototype</code> are shared across all
                    instances. Methods added inside a constructor are duplicated for
                    every single instance, consuming significantly more memory.
                </p>
                <CodeBlock language="javascript" code={memoryEfficiencyCode} />
                <ProTip><strong> Architect's Rule:</strong> Use the <code>prototype</code>
                    for methods (behavior) and the <code>constructor</code> for
                    instance-specific data (state).</ProTip>
            </div>

            <div className="content-section">
                <h2>The Pure Object Pattern</h2>
                <p>
                    Sometimes you need a hash map that is completely empty, without
                    built-in methods like <code>toString</code> or <code>hasOwnProperty</code>.
                    This prevents <strong>Prototype Pollution</strong> attacks where an
                    attacker might overwrite shared methods.
                </p>
                <CodeBlock language="javascript" code={nullPrototypeCode} />
            </div>

            <div className="content-section">
                <h2>__proto__ vs .prototype</h2>
                <p>
                    This is the most common point of confusion in JavaScript.
                    The <code>prototype</code> property is used by <strong>Constructor
                        Functions</strong> to set the prototype of new instances.
                    The <code>__proto__</code> property is the <strong>actual
                        link</strong> on the instance itself.
                </p>
                <CodeBlock language="javascript" code={internalMechanicsCode} />
                <div className="key-point">
                    In modern development, we use <code>Object.getPrototypeOf()</code>
                    instead of <code>__proto__</code> for better performance and
                    compatibility.
                </div>
            </div>

            <KeyFacts><ul>
                    <li><strong>Hierarchy:</strong> Most objects eventually trace back to <code>Object.prototype</code>.</li>
                    <li><strong>Shadowing:</strong> If an object has its own property with the same name as a prototype property, it "shadows" it.</li>
                    <li><strong>Efficiency:</strong> Use prototypes to prevent creating 10,000 function instances for 10,000 objects.</li>
                    <li> <strong>Mutation:</strong> Modifying <code>Object.prototype</code> is a major anti-pattern that can break entire libraries.</li>
                    <li><strong>Modernity:</strong> ES6 Classes use this exact system under the hood; prototypes are the engine, Classes are the dashboard.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSPrototypes;


